//@ts-nocheck
// src/MapWidget.tsx
import React, { useRef, useEffect } from 'react';
import DeckGL from '@deck.gl/react';
import { Map } from 'react-map-gl/maplibre';
import { setDeckInstance, handleLayerVisibilityChange, handlePanZoom } from './layerManager';
import debounce from 'lodash/debounce';

export function MapWidget({
  sicLetters,
  isDataLoading,
}) {
  let currentViewState;
  const deckRef = useRef(null);

  useEffect(() => {
    if (deckRef.current && deckRef.current.deck) {
      setDeckInstance(deckRef.current.deck);
    }
  }, []);

  // Debounce triggerUpdate to limit calls during rapid navigation
  const debouncedLogH3Cells = debounce((viewState) => {
    try {
      currentViewState = viewState;
      handlePanZoom(viewState);
      deckRef.current.deck.redraw(true);
    } catch (error) {
      console.error('Error calling triggerUpdate:', error);
    }
  }, 100);

  return (
    <div style={{ position: 'relative', height: '100vh' }}>
      <h1
        className="shine-text"
        style={{
          position: 'absolute',
          top: '10px',
          right: '20px',
          margin: 0,
          fontFamily: "'Poppins', sans-serif",
          fontSize: '36px',
          fontWeight: 600,
          color: '#fff',
          textShadow: '0 0 4px rgba(255, 255, 255, 0.8)',
          background: '#1a2a6c',
          padding: '8px 16px',
          borderRadius: '12px',
          zIndex: 1001, // Above map and isDataLoading message
        }}
      >
        JobWhere
      </h1>
      {isDataLoading && (
        <div
          style={{
            position: 'absolute',
            top: '10px',
            left: '10px',
            background: 'rgba(255, 255, 255, 0.8)',
            padding: '5px 10px',
            borderRadius: '3px',
            zIndex: 1000, // Below logo
          }}
        >
          Loading data...
        </div>
      )}
      <div
        style={{
          position: 'absolute',
          top: '140px',
          left: '10px',
          background: 'white',
          padding: '10px',
          borderRadius: '5px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
          zIndex: '1',
          width: '220px',
        }}
      >
        {[
          { letter: 'A', name: 'Agriculture, Forestry and Fishing', color: 'rgba(255, 0, 0, 0.784)' },
          { letter: 'B', name: 'Mining and Quarrying', color: 'rgba(255, 69, 0, 0.784)' },
          { letter: 'C', name: 'Manufacturing', color: 'rgba(255, 140, 0, 0.784)' },
          { letter: 'D', name: 'Electricity, Gas, Steam and Air Conditioning Supply', color: 'rgba(255, 165, 0, 0.784)' },
          { letter: 'E', name: 'Water Supply, Sewerage, Waste Management', color: 'rgba(255, 255, 0, 0.784)' },
          { letter: 'F', name: 'Construction', color: 'rgba(204, 255, 0, 0.784)' },
          { letter: 'G', name: 'Wholesale and Retail Trade', color: 'rgba(173, 255, 47, 0.784)' },
          { letter: 'H', name: 'Transportation and Storage', color: 'rgba(127, 255, 0, 0.784)' },
          { letter: 'I', name: 'Accommodation and Food Service Activities', color: 'rgba(50, 205, 50, 0.784)' },
          { letter: 'J', name: 'Information and Communication', color: 'rgba(0, 128, 0, 0.784)' },
          { letter: 'K', name: 'Financial and Insurance Activities', color: 'rgba(0, 255, 255, 0.784)' },
          { letter: 'L', name: 'Real Estate Activities', color: 'rgba(0, 206, 209, 0.784)' },
          { letter: 'M', name: 'Professional, Scientific and Technical Activities', color: 'rgba(0, 191, 255, 0.784)' },
          { letter: 'N', name: 'Administrative and Support Service Activities', color: 'rgba(0, 0, 255, 0.784)' },
          { letter: 'O', name: 'Public Administration and Defence', color: 'rgba(0, 0, 128, 0.784)' },
          { letter: 'P', name: 'Education', color: 'rgba(75, 0, 130, 0.784)' },
          { letter: 'Q', name: 'Human Health and Social Work Activities', color: 'rgba(138, 43, 226, 0.784)' },
          { letter: 'R', name: 'Arts, Entertainment and Recreation', color: 'rgba(255, 0, 255, 0.784)' },
          { letter: 'S', name: 'Other Service Activities', color: 'rgba(128, 0, 128, 0.784)' },
        ].map(section => (
          <div key={section.letter} style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
            <input
              type="checkbox"
              id={`sic-${section.letter}`}
              defaultChecked={true}
              onChange={() => {
                const checkedLetters = sicLetters.filter(letter => {
                  const checkbox = document.getElementById(`sic-${letter}`) as HTMLInputElement;
                  return checkbox && checkbox.checked;
                });
                handleLayerVisibilityChange(checkedLetters, deckRef.current.deck.viewState);
                deckRef.current.deck.redraw(true);
              }}
              disabled={false}
              style={{ marginRight: '8px' }}
            />
            <label htmlFor={`sic-${section.letter}`} style={{ fontSize: '12px', flexGrow: 1 }}>
              {section.name}
            </label>
            <div
              style={{
                width: '10px',
                height: '10px',
                backgroundColor: section.color,
                marginLeft: '8px',
                flexShrink: 0,
              }}
            />
          </div>
        ))}
        <div style={{ display: 'flex', gap: '5px', marginTop: '10px' }}>
          <button
            onClick={() => {
              sicLetters.forEach(letter => {
                const checkbox = document.getElementById(`sic-${letter}`) as HTMLInputElement;
                if (checkbox) checkbox.checked = true;
              });
              handleLayerVisibilityChange(["*"], deckRef.current.deck.viewState);
              deckRef.current.deck.redraw(true);
            }}
            style={{
              flex: 1,
              padding: '5px',
              background: '#f0f0f0',
              border: '1px solid #ccc',
              borderRadius: '3px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Select All
          </button>
          <button
            onClick={() => {
              sicLetters.forEach(letter => {
                const checkbox = document.getElementById(`sic-${letter}`) as HTMLInputElement;
                if (checkbox) checkbox.checked = false;
              });
              handleLayerVisibilityChange([], deckRef.current.deck.viewState);
              deckRef.current.deck.redraw(true);
            }}
            style={{
              flex: 1,
              padding: '5px',
              background: '#f0f0f0',
              border: '1px solid #ccc',
              borderRadius: '3px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            Select None
          </button>
        </div>
      </div>

      <DeckGL
        ref={deckRef}
        initialViewState={{
          longitude: -2.7,
          latitude: 54.3,
          zoom: 5.3,
          maxZoom: 18,
          pitch: 0,
          bearing: 0,
        }}
        controller={{ doubleClickZoom: false, dragPan: true, dragRotate: true, scrollZoom: true, touchZoom: true, touchRotate: true }}
        layers={[]}
        getTooltip={({ object }) => {
          if (object != undefined && object.companyname != '' && currentViewState.zoom > 10) {
            return { text: object.companyname };
          }
        }}
        onViewStateChange={({ viewState }) => {
          debouncedLogH3Cells(viewState);
          return viewState;
        }}
        useDevicePixels = {false}
      >
        <Map reuseMaps mapStyle='https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json' />
      </DeckGL>
      <style>
        {`
          .shine-text {
            animation: shine 1.5s ease-in-out infinite;
          }
          @keyframes shine {
            0% { text-shadow: 0 0 4px rgba(255, 255, 255, 0.8); }
            50% { text-shadow: 0 0 8px rgba(255, 255, 255, 1); }
            100% { text-shadow: 0 0 4px rgba(255, 255, 255, 0.8); }
          }
        `}
      </style>
    </div>
  );
}